import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  styled,
  TablePagination,
  InputAdornment,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Tabs,
  Tab,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReportsHubBack } from '../../../reports/hub/useReportsHubBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import CancelIcon from '@mui/icons-material/Cancel';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import { useNavigate } from 'react-router-dom';
import ownAuthSession from '../../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../../services/FirebaseDataService';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { tr } from 'date-fns/locale';
import {
  restaurantActiveSaleApi,
  restaurantActiveSaleItemApi,
  restaurantCompletedSaleApi,
  restaurantScreenApi,
  RestaurantActiveSaleDto,
  RestaurantActiveSaleItemDto,
  RestaurantCompletedSaleDto
} from '../../../../services/restaurantApiService';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../../common/guide/GuideButton';

// ═══════════════════════════════════════════════════════════
// 🎨 STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════

const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: '#f5f7fa',
  minHeight: '100%',
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
  },
}));

// ═══════════════════════════════════════════════════════════
// 🔧 INTERFACES
// ═══════════════════════════════════════════════════════════

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface CompletedSaleItem {
  id: number;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountPercent?: number;
  discountAmount?: number;
  status?: string;
  notes?: string;
  addedAt?: string;
  addedBy?: string;
}

interface WaiterAction {
  // Eski yapı
  waiterName?: string;
  waiterId?: number;
  actionType?: string;
  timestamp?: string;
  productName?: string;
  quantity?: number;
  // Yeni yapı
  action?: string;
  waiter?: string;
  time?: string;
  details?: {
    productId?: string;
    productName?: string;
    quantity?: number;
    unitPrice?: number;
    sqlItemId?: number;
    tableName?: string;
    tableId?: string;
    openedAt?: string;
    [key: string]: any;
  };
}

// Ürün iptalleri için interface
interface ProductCancellation {
  saleId: number;
  saleNumber: string;
  tableName: string;
  tableGroupName?: string;
  screenName?: string;
  waiterName: string;
  productId?: string;
  productName: string;
  quantity: number;
  totalPrice?: number;
  unitPrice?: number;
  oldQuantity?: number;
  newQuantity?: number;
  change?: number;
  actionType: 'remove_item' | 'update_quantity';
  reason?: string;
  timestamp: string;
  completedAt: string;
}

// ═══════════════════════════════════════════════════════════
// 🛠️ HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════

const formatDate = (dateString: string, addTurkeyOffset = false) => {
  try {
    let date = new Date(dateString);
    // Aktif masalar için UTC'den gelen saate +3 saat ekle
    if (addTurkeyOffset) {
      date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
    }
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  } catch (error) {
    return dateString;
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDuration = (minutes: number | null | undefined) => {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}s ${mins}dk`;
  }
  return `${mins}dk`;
};

const getPaymentMethodType = (method: string): 'cash' | 'creditCard' | 'partial' | 'unknown' => {
  switch (method?.toLowerCase()) {
    case 'nakit':
    case 'cash':
      return 'cash';
    case 'kredi kartı':
    case 'credit_card':
    case 'pos':
      return 'creditCard';
    case 'parçalı':
    case 'partial':
      return 'partial';
    default:
      return 'unknown';
  }
};

// WaiterActionsJson'ı parse et
const parseWaiterActionsJson = (waiterActionsJson: string | null | undefined): WaiterAction[] => {
  if (!waiterActionsJson) return [];
  try {
    return JSON.parse(waiterActionsJson) || [];
  } catch {
    return [];
  }
};

// Garson adını WaiterActionsJson'dan çıkar
const getWaiterNameFromActions = (waiterActionsJson: string | null | undefined): string => {
  const actions = parseWaiterActionsJson(waiterActionsJson);
  if (actions.length === 0) return '-';
  
  // Son işlemi yapan garsonun adını al (yeni ve eski yapı desteği)
  const lastAction = actions[actions.length - 1];
  return lastAction?.waiter || lastAction?.waiterName || '-';
};

// Action'dan garson adını al (normalize)
const getWaiterName = (action: WaiterAction): string => {
  return action.waiter || action.waiterName || '-';
};

// Action'dan zaman bilgisini al (normalize)
const getActionTime = (action: WaiterAction): string | undefined => {
  return action.time || action.timestamp;
};

// Action'dan ürün adını al (normalize)
const getActionProductName = (action: WaiterAction): string | undefined => {
  return action.details?.productName || action.productName;
};

// Action'dan miktar al (normalize)
const getActionQuantity = (action: WaiterAction): number | undefined => {
  return action.details?.quantity || action.quantity;
};

// Action'dan detay bilgisini al
const getActionDetails = (action: WaiterAction): string => {
  const actionType = (action.action || action.actionType || '').toLowerCase();

  if (actionType === 'transfer_items' || actionType === 'transfer_items_close' || actionType === 'receive_transferred_items') {
    const d = action.details;
    if (!d) return '-';
    const itemNames = d.items?.map((i: any) => `${i.productName} (x${i.quantity})`).join(', ') || '';
    const direction = actionType === 'receive_transferred_items'
      ? `${d.fromTableName || '?'} → ${d.toTableName || '?'}`
      : `${d.fromTableName || '?'} → ${d.toTableName || '?'}`;
    const reason = d.reason ? ` | ${d.reason}` : '';
    return `${direction} | ${d.itemCount || 0} ürün: ${itemNames}${reason}`;
  }

  // Sepet böl hareketleri
  if (actionType.startsWith('split_')) {
    const d: Record<string, any> = action.details || {};
    const tr = (n: number) => Number(n || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
    const split = d.splitCode || (d.splitIndex ? `Sepet #${d.splitIndex}` : 'bölünen sepet');
    switch (actionType) {
      case 'split_created':
        return `Bölünen sepet açıldı: ${split}`;
      case 'split_items_out':
        return `${d.productName} (x${d.quantity}) → ${split}`;
      case 'split_items_in':
        return `${d.productName} (x${d.quantity}) ana sepetten alındı`;
      case 'split_items_back':
        return `${d.productName} (x${d.quantity}) ${split} → ana sepet`;
      case 'split_paid': {
        const items = (d.items || []).map((i: any) => `${i.productName} (x${i.quantity})`).join(', ');
        const no = d.invoiceNumber || d.saleNumber || split;
        return `${no} | ${d.paymentMethod || '-'} | ${tr(d.totalAmount)}${items ? ` | ${items}` : ''}`;
      }
      default:
        break;
    }
  }

  const productName = getActionProductName(action);
  const quantity = getActionQuantity(action);
  
  if (productName) {
    return quantity ? `${productName} (x${quantity})` : productName;
  }
  
  if (action.details?.tableName) {
    return `Masa: ${action.details.tableName}`;
  }
  
  return '-';
};

// Aksiyon tipini Türkçe'ye çevir
const getActionTypeLabel = (actionType: string | undefined): string => {
  switch (actionType?.toLowerCase()) {
    case 'add_product':
    case 'product_added':
    case 'add_item':
      return 'Ürün Ekledi';
    case 'remove_product':
    case 'product_removed':
    case 'remove_item':
      return 'Ürün Sildi';
    case 'update_quantity':
    case 'quantity_updated':
      return 'Miktar Güncelledi';
    case 'print_kitchen':
    case 'kitchen_printed':
    case 'print_to_kitchen':
      return 'Mutfağa Yazdırdı';
    case 'close_table':
    case 'table_closed':
      return 'Masa Kapattı';
    case 'open_table':
    case 'table_opened':
      return 'Masa Açtı';
    case 'apply_discount':
    case 'discount_applied':
      return 'İndirim Uyguladı';
    case 'transfer_table':
    case 'table_transferred':
      return 'Masa Taşıdı';
    case 'transfer_items':
      return 'Ürün Aktardı';
    case 'transfer_items_close':
      return 'Ürün Aktardı (Adisyon Kapatıldı)';
    case 'receive_transferred_items':
      return 'Ürün Alındı (Aktarma)';
    case 'clear_cart':
      return 'Sepet Temizledi';
    case 'split_created':
      return 'Sepet Böldü';
    case 'split_items_out':
      return 'Sepet Böl: Ürün Ayırdı';
    case 'split_items_in':
      return 'Sepet Böl: Ürün Geldi';
    case 'split_items_back':
      return 'Sepet Böl: Ürün Geri Aldı';
    case 'split_paid':
      return 'Bölünen Sepet Ödendi';
    case 'payment':
    case 'complete_payment':
      return 'Ödeme Aldı';
    case 'cancel_check':
    case 'check_cancelled':
      return '❌ Adisyon İptal Etti';
    default:
      return actionType || 'İşlem';
  }
};

// TabPanel Component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`orders-tabpanel-${index}`}
      aria-labelledby={`orders-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 🚀 MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

interface RestaurantOrdersReportProps {
  /** Yönetici paneli: yalnız bu şubenin ekranları ve satışları gösterilir */
  lockedBranchId?: string | null;
  /** Gömülü modda geri butonu route'a dönmek yerine bunu çağırır */
  onBack?: () => void;
}

const RestaurantOrdersReport: React.FC<RestaurantOrdersReportProps> = ({ lockedBranchId = null, onBack }) => {
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  const { goBack } = useReportsHubBack('/restaurant-orders', () => (onBack ? onBack() : navigate('/restoran')));

  // ─────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Restoran ekranları listesi (filtre için)
  const [restaurantScreens, setRestaurantScreens] = useState<{ id: number; name: string }[]>([]);
  const [selectedScreenId, setSelectedScreenId] = useState<number | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Data states
  const [activeSales, setActiveSales] = useState<RestaurantActiveSaleDto[]>([]);
  const [completedSales, setCompletedSales] = useState<RestaurantCompletedSaleDto[]>([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<string>('all');
  // ✅ YENİ: Varsayılan olarak bugünün başlangıcı ve sonu
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Günün başlangıcı
    return today;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Günün sonu
    return today;
  });
  const [showFilters, setShowFilters] = useState(false); // ✅ Varsayılan olarak gizli

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Ürün İptalleri Pagination (sabit 10 satır)
  const [productCancellationsPage, setProductCancellationsPage] = useState(0);

  // UI states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('info');
  const [loadingExport, setLoadingExport] = useState(false);

  // Details modal (Tamamlanan siparişler için)
  const [selectedSale, setSelectedSale] = useState<RestaurantCompletedSaleDto | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  // Details modal (Aktif masalar için)
  const [selectedActiveSale, setSelectedActiveSale] = useState<RestaurantActiveSaleDto | null>(null);
  const [activeDetailsModalOpen, setActiveDetailsModalOpen] = useState(false);
  const [activeSaleItems, setActiveSaleItems] = useState<RestaurantActiveSaleItemDto[]>([]);
  const [loadingActiveSaleItems, setLoadingActiveSaleItems] = useState(false);

  // Waiter Actions modal (Her iki tab için)
  const [waiterActionsModalOpen, setWaiterActionsModalOpen] = useState(false);
  const [selectedWaiterActions, setSelectedWaiterActions] = useState<WaiterAction[]>([]);
  const [selectedWaiterActionsTitle, setSelectedWaiterActionsTitle] = useState('');
  const [selectedWaiterActionsCancellationReason, setSelectedWaiterActionsCancellationReason] = useState<string | null>(null);

  const getLocalizedPaymentMethod = useCallback((method: string) => {
    const type = getPaymentMethodType(method);
    if (type === 'cash') return { label: t('restaurantOrdersReport.filters.cash'), color: 'success' as const };
    if (type === 'creditCard') return { label: t('restaurantOrdersReport.filters.creditCard'), color: 'primary' as const };
    if (type === 'partial') return { label: t('restaurantOrdersReport.filters.partial'), color: 'warning' as const };
    return { label: method || t('restaurantOrdersReport.payment.unknown'), color: 'default' as const };
  }, [t]);

  const formatDurationLabel = useCallback((minutes: number | null | undefined) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return t('restaurantOrdersReport.duration.hoursMinutes', { hours, minutes: mins });
    }
    return t('restaurantOrdersReport.duration.minutes', { minutes: mins });
  }, [t]);

  // ─────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────

  // İlk yükleme - şirket ve restoran ekranlarını yükle
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Kullanıcı bilgilerini al
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError(t('restaurantOrdersReport.errors.noSession'));
        setLoading(false);
        return;
      }

      // Şirket ID'sini al
      const userData = await firebaseDataService.getUserData(uid);
      if (!userData) {
        setError(t('restaurantOrdersReport.errors.userInfoMissing'));
        setLoading(false);
        return;
      }

      const compId = userData.companyId;
      setCompanyId(compId);

      if (!compId) {
        setError(t('restaurantOrdersReport.errors.companyMissing'));
        setLoading(false);
        return;
      }

      // Tüm restoran ekranlarını yükle (filtre için)
      let screensList: { id: number; name: string }[] = [];
      try {
        const screensData = await restaurantScreenApi.getByCompanyId();
        screensList = screensData
          .filter(s => !lockedBranchId || s.BranchId === lockedBranchId)
          .map(s => ({ id: s.Id, name: s.Name }));
        setRestaurantScreens(screensList);
        console.log('✅ Restoran ekranları yüklendi:', screensList.length);
      } catch (screenError) {
        console.error('Restoran ekranları yüklenemedi:', screenError);
      }

      // Tüm ekranlardan aktif satışları topla
      let allActiveSales: RestaurantActiveSaleDto[] = [];
      for (const screen of screensList) {
        try {
          const screenActiveSales = await restaurantActiveSaleApi.getByRestaurantScreenId(screen.id);
          allActiveSales = [...allActiveSales, ...screenActiveSales];
        } catch (err) {
          console.warn(`Ekran ${screen.id} aktif satışları yüklenemedi`);
        }
      }
      setActiveSales(allActiveSales);
      console.log('✅ Tüm aktif satışlar yüklendi:', allActiveSales.length);

      // Tamamlanmış satışları yükle (tüm şirket için)
      try {
        const completedSalesData = await restaurantCompletedSaleApi.getByCompanyId(
          startDate?.toISOString(),
          endDate?.toISOString(),
          lockedBranchId
        );
        setCompletedSales(completedSalesData);
        console.log('✅ Şirket tamamlanmış satışları yüklendi:', completedSalesData.length);
      } catch (apiError) {
        console.error('API hatası:', apiError);
        setError(t('restaurantOrdersReport.errors.dataLoadFailed'));
      }

      setLoading(false);
    } catch (err: any) {
      console.error('❌ Veri yüklenirken hata:', err);
      setError(`${t('restaurantOrdersReport.errors.dataLoadFailed')}: ${err.message}`);
      setLoading(false);
    }
  }, [startDate, endDate, lockedBranchId]);

  // Filtre ile veri yükleme
  const loadData = useCallback(async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      setError(null);

      // Seçili ekrana göre verileri yükle
      if (selectedScreenId) {
        try {
          // Aktif satışları yükle (seçili ekran için)
          const activeSalesData = await restaurantActiveSaleApi.getByRestaurantScreenId(selectedScreenId);
          setActiveSales(activeSalesData);
          console.log('✅ Aktif satışlar yüklendi:', activeSalesData.length);

          // Tamamlanmış satışları yükle (seçili ekran için)
          const completedSalesData = await restaurantCompletedSaleApi.getByRestaurantScreenId(
            selectedScreenId,
            startDate?.toISOString(),
            endDate?.toISOString()
          );
          setCompletedSales(completedSalesData);
          console.log('✅ Tamamlanmış satışlar yüklendi:', completedSalesData.length);
        } catch (apiError) {
          console.error('API hatası:', apiError);
          setError(t('restaurantOrdersReport.errors.dataLoadFailed'));
        }
      } else {
        // Tüm şirket için yükle
        try {
          // Tüm ekranlardan aktif satışları topla
          let allActiveSales: RestaurantActiveSaleDto[] = [];
          for (const screen of restaurantScreens) {
            try {
              const screenActiveSales = await restaurantActiveSaleApi.getByRestaurantScreenId(screen.id);
              allActiveSales = [...allActiveSales, ...screenActiveSales];
            } catch (err) {
              console.warn(`Ekran ${screen.id} aktif satışları yüklenemedi`);
            }
          }
          setActiveSales(allActiveSales);
          console.log('✅ Tüm aktif satışlar yüklendi:', allActiveSales.length);

          // Tamamlanmış satışları yükle (tüm şirket için)
          const completedSalesData = await restaurantCompletedSaleApi.getByCompanyId(
            startDate?.toISOString(),
            endDate?.toISOString(),
            lockedBranchId
          );
          setCompletedSales(completedSalesData);
          console.log('✅ Şirket tamamlanmış satışları yüklendi:', completedSalesData.length);
        } catch (apiError) {
          console.error('API hatası:', apiError);
          setError(t('restaurantOrdersReport.errors.dataLoadFailed'));
        }
      }

      setLoading(false);
    } catch (err: any) {
      console.error('❌ Veri yüklenirken hata:', err);
      setError(`${t('restaurantOrdersReport.errors.dataLoadFailed')}: ${err.message}`);
      setLoading(false);
    }
  }, [companyId, selectedScreenId, startDate, endDate, restaurantScreens, lockedBranchId]);

  // İlk yükleme
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // ─────────────────────────────────────────────────────────
  // FILTERING
  // ─────────────────────────────────────────────────────────

  const filteredActiveSales = useMemo(() => {
    let filtered = [...activeSales];

    // Arama filtresi
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(sale =>
        sale.TableName?.toLowerCase().includes(lowerSearch) ||
        sale.Notes?.toLowerCase().includes(lowerSearch)
      );
    }

    // Tarihe göre sırala (en yeni en üstte)
    filtered.sort((a, b) => new Date(b.OpenedAt).getTime() - new Date(a.OpenedAt).getTime());

    return filtered;
  }, [activeSales, searchTerm]);

  // Ödeme yöntemi normal mi (tamamlanmış) kontrol fonksiyonu
  const isCompletedPayment = (paymentMethod: string | null | undefined): boolean => {
    if (!paymentMethod) return false;
    const normalizedMethod = paymentMethod.toLowerCase();
    const completedMethods = ['nakit', 'cash', 'pos', 'kredi kartı', 'credit_card', 'paid', 'parçalı', 'partial'];
    return completedMethods.includes(normalizedMethod);
  };

  // Ödeme yöntemi iptal mi kontrol fonksiyonu
  const isCancelledPayment = (paymentMethod: string | null | undefined): boolean => {
    if (!paymentMethod) return true; // PaymentMethod yoksa iptal sayılır
    const normalizedMethod = paymentMethod.toLowerCase();
    const cancelledMethods = ['unpaid', 'cancelled', 'iptal', 'cancel'];
    return cancelledMethods.includes(normalizedMethod) || !isCompletedPayment(paymentMethod);
  };

  const filteredCompletedSales = useMemo(() => {
    // Sadece tamamlanmış (ödeme alınmış) satışları filtrele
    let filtered = completedSales.filter(sale => isCompletedPayment(sale.PaymentMethod));

    // Arama filtresi
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(sale =>
        sale.TableName?.toLowerCase().includes(lowerSearch) ||
        sale.SaleNumber?.toLowerCase().includes(lowerSearch) ||
        sale.CompletedByName?.toLowerCase().includes(lowerSearch) ||
        sale.Notes?.toLowerCase().includes(lowerSearch)
      );
    }

    // Ödeme yöntemi filtresi
    if (filterPaymentMethod !== 'all') {
      filtered = filtered.filter(sale =>
        sale.PaymentMethod?.toLowerCase() === filterPaymentMethod.toLowerCase()
      );
    }

    // Tarihe göre sırala (en yeni en üstte)
    filtered.sort((a, b) => new Date(b.CompletedAt).getTime() - new Date(a.CompletedAt).getTime());

    return filtered;
  }, [completedSales, searchTerm, filterPaymentMethod]);

  // ✅ YENİ: İptal edilmiş adisyonları filtrele
  const filteredCancelledSales = useMemo(() => {
    // Sadece iptal edilmiş (ödeme alınmamış) satışları filtrele
    let filtered = completedSales.filter(sale => isCancelledPayment(sale.PaymentMethod));

    // Arama filtresi
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(sale =>
        sale.TableName?.toLowerCase().includes(lowerSearch) ||
        sale.SaleNumber?.toLowerCase().includes(lowerSearch) ||
        sale.CompletedByName?.toLowerCase().includes(lowerSearch) ||
        sale.Notes?.toLowerCase().includes(lowerSearch)
      );
    }

    // Tarihe göre sırala (en yeni en üstte)
    filtered.sort((a, b) => new Date(b.CompletedAt).getTime() - new Date(a.CompletedAt).getTime());

    return filtered;
  }, [completedSales, searchTerm]);

  // ✅ YENİ: Ürün iptallerini tüm completedSales'den çıkar
  const productCancellations = useMemo(() => {
    const cancellations: ProductCancellation[] = [];
    
    // Tüm satışları tara (hem tamamlananlar hem iptaller)
    completedSales.forEach(sale => {
      // WaiterActionsJson'u parse et
      const actions = parseWaiterActionsJson(sale.WaiterActionsJson);
      
      // Restoran ekran adını bul
      const screen = restaurantScreens.find(s => s.id === sale.RestaurantScreenId);
      
      actions.forEach(action => {
        const actionType = action.action || action.actionType;
        
        // Sadece ürün silme ve miktar azaltma işlemleri
        if (actionType === 'remove_item') {
          cancellations.push({
            saleId: sale.Id,
            saleNumber: sale.SaleNumber || '',
            tableName: sale.TableName || t('restaurantOrdersReport.table.tableWithId', { id: sale.TableId }),
            tableGroupName: sale.TableGroupName || undefined,
            screenName: screen?.name || undefined,
            waiterName: action.waiter || action.waiterName || '-',
            productId: action.details?.productId || undefined,
            productName: action.details?.productName || action.productName || t('restaurantOrdersReport.unknownProduct'),
            quantity: action.details?.quantity || action.quantity || 1,
            totalPrice: action.details?.totalPrice || undefined,
            unitPrice: action.details?.unitPrice || undefined,
            actionType: 'remove_item',
            reason: action.details?.reason || undefined,
            timestamp: action.time || action.timestamp || '',
            completedAt: sale.CompletedAt
          });
        } else if (actionType === 'update_quantity' && action.details?.change && action.details.change < 0) {
          // Miktar azaltma (change negatif ise)
          cancellations.push({
            saleId: sale.Id,
            saleNumber: sale.SaleNumber || '',
            tableName: sale.TableName || t('restaurantOrdersReport.table.tableWithId', { id: sale.TableId }),
            tableGroupName: sale.TableGroupName || undefined,
            screenName: screen?.name || undefined,
            waiterName: action.waiter || action.waiterName || '-',
            productId: action.details?.productId || undefined,
            productName: action.details?.productName || action.productName || t('restaurantOrdersReport.unknownProduct'),
            quantity: Math.abs(action.details.change),
            oldQuantity: action.details.oldQuantity,
            newQuantity: action.details.newQuantity,
            change: action.details.change,
            unitPrice: action.details?.unitPrice || undefined,
            actionType: 'update_quantity',
            timestamp: action.time || action.timestamp || '',
            completedAt: sale.CompletedAt
          });
        }
      });
    });
    
    // Arama filtresi
    let filtered = cancellations;
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.productName.toLowerCase().includes(lowerSearch) ||
        c.tableName.toLowerCase().includes(lowerSearch) ||
        c.waiterName.toLowerCase().includes(lowerSearch) ||
        c.saleNumber.toLowerCase().includes(lowerSearch) ||
        c.screenName?.toLowerCase().includes(lowerSearch)
      );
    }
    
    // Tarihe göre sırala (en yeni en üstte)
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    return filtered;
  }, [completedSales, searchTerm, restaurantScreens]);

  // ─────────────────────────────────────────────────────────
  // CALCULATIONS
  // ─────────────────────────────────────────────────────────

  const summaryStats = useMemo(() => {
    const activeTotal = filteredActiveSales.reduce((sum, sale) => sum + (sale.TotalAmount || 0), 0);
    const completedTotal = filteredCompletedSales.reduce((sum, sale) => sum + (sale.TotalAmount || 0), 0);
    const cancelledTotal = filteredCancelledSales.reduce((sum, sale) => sum + (sale.TotalAmount || 0), 0);
    const avgDuration = filteredCompletedSales.length > 0
      ? filteredCompletedSales.reduce((sum, sale) => sum + (sale.DurationMinutes || 0), 0) / filteredCompletedSales.length
      : 0;

    return {
      activeCount: filteredActiveSales.length,
      activeTotal,
      completedCount: filteredCompletedSales.length,
      completedTotal,
      cancelledCount: filteredCancelledSales.length,
      productCancellationsCount: productCancellations.length,
      cancelledTotal,
      avgDuration: Math.round(avgDuration)
    };
  }, [filteredActiveSales, filteredCompletedSales, filteredCancelledSales, productCancellations]);

  // ─────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (sale: RestaurantCompletedSaleDto) => {
    setSelectedSale(sale);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedSale(null);
  };

  // Aktif masa detayları
  const handleViewActiveDetails = async (sale: RestaurantActiveSaleDto) => {
    setSelectedActiveSale(sale);
    setActiveDetailsModalOpen(true);
    setLoadingActiveSaleItems(true);
    
    try {
      // Aktif satışın ürünlerini çek
      const items = await restaurantActiveSaleItemApi.getByActiveSaleId(sale.Id);
      setActiveSaleItems(items);
      console.log('✅ Aktif masa ürünleri yüklendi:', items.length);
    } catch (error) {
      console.error('❌ Aktif masa ürünleri yüklenemedi:', error);
      setActiveSaleItems([]);
    } finally {
      setLoadingActiveSaleItems(false);
    }
  };

  const handleCloseActiveDetailsModal = () => {
    setActiveDetailsModalOpen(false);
    setSelectedActiveSale(null);
    setActiveSaleItems([]);
  };

  // Garson işlemleri modalı
  const handleViewWaiterActions = (waiterActionsJson: string | null | undefined, title: string, cancellationReason?: string | null) => {
    const actions = parseWaiterActionsJson(waiterActionsJson);
    setSelectedWaiterActions(actions);
    setSelectedWaiterActionsTitle(title);
    setSelectedWaiterActionsCancellationReason(cancellationReason || null);
    setWaiterActionsModalOpen(true);
  };

  const handleCloseWaiterActionsModal = () => {
    setWaiterActionsModalOpen(false);
    setSelectedWaiterActions([]);
    setSelectedWaiterActionsTitle('');
    setSelectedWaiterActionsCancellationReason(null);
  };

  const handleRefresh = () => {
    loadData();
    setSnackbarMessage(t('restaurantOrdersReport.snackbar.dataRefreshed'));
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  // ─────────────────────────────────────────────────────────
  // EXCEL EXPORT
  // ─────────────────────────────────────────────────────────

  const exportToExcel = async () => {
    try {
      setLoadingExport(true);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('restaurantOrdersReport.excel.sheetName'));

      // Sütunları tanımla
      worksheet.columns = [
        { header: t('restaurantOrdersReport.excel.saleNumber'), key: 'saleNumber', width: 15 },
        { header: t('restaurantOrdersReport.table.table'), key: 'tableName', width: 15 },
        { header: t('restaurantOrdersReport.excel.tableGroup'), key: 'tableGroupName', width: 15 },
        { header: t('restaurantOrdersReport.excel.openedAt'), key: 'openedAt', width: 20 },
        { header: t('restaurantOrdersReport.excel.closedAt'), key: 'completedAt', width: 20 },
        { header: t('restaurantOrdersReport.excel.durationMinutes'), key: 'duration', width: 12 },
        { header: t('restaurantOrdersReport.table.subTotal'), key: 'subTotal', width: 15 },
        { header: t('restaurantOrdersReport.excel.discount'), key: 'discount', width: 15 },
        { header: t('restaurantOrdersReport.excel.tax'), key: 'tax', width: 15 },
        { header: t('restaurantOrdersReport.table.total'), key: 'total', width: 15 },
        { header: t('restaurantOrdersReport.excel.payment'), key: 'paymentMethod', width: 15 },
        { header: t('restaurantOrdersReport.table.closedBy'), key: 'completedBy', width: 20 },
        { header: t('restaurantOrdersReport.excel.note'), key: 'notes', width: 30 }
      ];

      // Başlık stili
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4CAF50' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };

      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });

      // Verileri ekle
      filteredCompletedSales.forEach(sale => {
        worksheet.addRow({
          saleNumber: sale.SaleNumber,
          tableName: sale.TableName,
          tableGroupName: sale.TableGroupName || '-',
          openedAt: formatDate(sale.OpenedAt, true),
          completedAt: formatDate(sale.CompletedAt, true),
          duration: sale.DurationMinutes || 0,
          subTotal: sale.SubTotal,
          discount: sale.DiscountAmount,
          tax: sale.TaxAmount,
          total: sale.TotalAmount,
          paymentMethod: getLocalizedPaymentMethod(sale.PaymentMethod).label,
          completedBy: getWaiterNameFromActions(sale.WaiterActionsJson) !== '-' 
            ? getWaiterNameFromActions(sale.WaiterActionsJson) 
            : (sale.CompletedByName || '-'),
          notes: sale.Notes || '-'
        });
      });

      // Zebra striping
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
          });
        }
      });

      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const screenNameForFile = selectedScreenId 
        ? restaurantScreens.find(s => s.id === selectedScreenId)?.name || 'Secili'
        : 'Tum_Ekranlar';
      const fileName = `Restoran_Siparis_Raporu_${screenNameForFile.replace(/\s+/g, '_')}_${currentDate}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);

      setSnackbarMessage(t('restaurantOrdersReport.snackbar.excelDownloaded'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Excel export hatası:', error);
      setSnackbarMessage(t('restaurantOrdersReport.snackbar.excelFailed'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingExport(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // PAGINATION
  // ─────────────────────────────────────────────────────────

  const paginatedActiveSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredActiveSales.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredActiveSales, page, rowsPerPage]);

  const paginatedCompletedSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredCompletedSales.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCompletedSales, page, rowsPerPage]);

  // ✅ YENİ: İptal edilmiş adisyonlar pagination
  const paginatedCancelledSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredCancelledSales.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCancelledSales, page, rowsPerPage]);

  // ✅ YENİ: Ürün iptalleri pagination (sabit 10 satır)
  const paginatedProductCancellations = useMemo(() => {
    const startIndex = productCancellationsPage * 10;
    return productCancellations.slice(startIndex, startIndex + 10);
  }, [productCancellations, productCancellationsPage]);

  // Ürün iptalleri sayfa değiştirme handler'ları
  const handleProductCancellationsPageChange = (_: unknown, newPage: number) => {
    setProductCancellationsPage(newPage);
  };

  // ─────────────────────────────────────────────────────────
  // PARSE ITEMS JSON
  // ─────────────────────────────────────────────────────────

  const parseItemsJson = (itemsJson: string): CompletedSaleItem[] => {
    try {
      return JSON.parse(itemsJson) || [];
    } catch {
      return [];
    }
  };

  // ═══════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════

  return (
    <ScrollableContent>
      {/* Header */}
      <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box display="flex" alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
          <IconButton
            onClick={goBack}
            color="primary"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <ReceiptLongIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: { xs: 1, md: 1.5 }, color: 'primary.main' }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                {t('restaurantOrdersReport.title')}
              </Typography>
              <GuideButton guideKey="sales.restaurantReport" sx={{ ml: 0.5 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
              {selectedScreenId 
                ? restaurantScreens.find(s => s.id === selectedScreenId)?.name || t('restaurantOrdersReport.selectedScreen')
                : t('restaurantOrdersReport.allScreens')
              }
            </Typography>
          </Box>
        </Box>

        {/* Desktop Butonlar */}
        <Box display={{ xs: 'none', md: 'flex' }} gap={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {t('restaurantOrdersReport.actions.refresh')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters ? 'primary' : 'inherit'}
          >
            {showFilters ? t('restaurantOrdersReport.actions.hideFilters') : t('restaurantOrdersReport.actions.filters')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={loadingExport || loading}
          >
            {loadingExport ? t('restaurantOrdersReport.actions.preparing') : t('restaurantOrdersReport.actions.downloadExcel')}
          </Button>
        </Box>

        {/* Mobil Butonlar */}
        <Box display={{ xs: 'flex', md: 'none' }} gap={1} width="100%" flexWrap="wrap">
          <IconButton
            color="primary"
            onClick={handleRefresh}
            disabled={loading}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
            }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
            sx={{ 
              bgcolor: showFilters ? 'primary.main' : 'grey.300',
              color: showFilters ? 'white' : 'grey.700',
              '&:hover': { bgcolor: showFilters ? 'primary.dark' : 'grey.400' }
            }}
          >
            <FilterListIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={exportToExcel}
            disabled={loadingExport || loading}
            sx={{ 
              bgcolor: 'success.main', 
              color: 'white',
              '&:hover': { bgcolor: 'success.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
            }}
          >
            <FileDownloadIcon />
          </IconButton>
        </Box>
      </HeaderContainer>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Şube Filtresi */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('restaurantOrdersReport.filters.branch')}</InputLabel>
                <Select
                  value={selectedScreenId || ''}
                  label={t('restaurantOrdersReport.filters.branch')}
                  onChange={(e) => setSelectedScreenId(e.target.value ? Number(e.target.value) : null)}
                >
                  <MenuItem value="">{t('restaurantOrdersReport.filters.allBranches')}</MenuItem>
                  {restaurantScreens.map((screen) => (
                    <MenuItem key={screen.id} value={screen.id}>
                      {screen.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('restaurantOrdersReport.filters.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('restaurantOrdersReport.filters.paymentMethod')}</InputLabel>
                <Select
                  value={filterPaymentMethod}
                  label={t('restaurantOrdersReport.filters.paymentMethod')}
                  onChange={(e) => setFilterPaymentMethod(e.target.value)}
                >
                  <MenuItem value="all">{t('restaurantOrdersReport.filters.all')}</MenuItem>
                  <MenuItem value="nakit">{t('restaurantOrdersReport.filters.cash')}</MenuItem>
                  <MenuItem value="kredi kartı">{t('restaurantOrdersReport.filters.creditCard')}</MenuItem>
                  <MenuItem value="parçalı">{t('restaurantOrdersReport.filters.partial')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <DatePicker
                    label={t('restaurantOrdersReport.filters.startDate')}
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                  <DatePicker
                    label={t('restaurantOrdersReport.filters.endDate')}
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Box>
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={loadData}
                disabled={loading}
              >
                {t('restaurantOrdersReport.actions.applyFilter')}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard>
            <TableRestaurantIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.summary.activeTables')}</Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {summaryStats.activeCount}
              </Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard>
            <TrendingUpIcon sx={{ fontSize: 40, color: 'info.main' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.summary.activeTotal')}</Typography>
              <Typography variant="h5" fontWeight="bold" color="info.main">
                {formatCurrency(summaryStats.activeTotal)}
              </Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.summary.completed')}</Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {summaryStats.completedCount}
              </Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard>
            <CancelIcon sx={{ fontSize: 40, color: 'error.main' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.summary.cancellations')}</Typography>
              <Typography variant="h5" fontWeight="bold" color="error.main">
                {formatCurrency(summaryStats.cancelledTotal)}
              </Typography>
            </Box>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <SummaryCard>
            <PaymentIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.summary.totalRevenue')}</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {formatCurrency(summaryStats.completedTotal)}
              </Typography>
            </Box>
          </SummaryCard>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Mobile Dropdown (xs, sm) */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('restaurantOrdersReport.viewSelect')}</InputLabel>
            <Select
              value={activeTab}
              label={t('restaurantOrdersReport.viewSelect')}
              onChange={(e) => setActiveTab(Number(e.target.value))}
            >
              <MenuItem value={0}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TableRestaurantIcon fontSize="small" />
                  {t('restaurantOrdersReport.tabs.activeTables', { count: summaryStats.activeCount })}
                </Box>
              </MenuItem>
              <MenuItem value={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CheckCircleIcon fontSize="small" />
                  {t('restaurantOrdersReport.tabs.completedOrders', { count: summaryStats.completedCount })}
                </Box>
              </MenuItem>
              <MenuItem value={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CancelIcon fontSize="small" color="error" />
                  {t('restaurantOrdersReport.tabs.receiptCancellations', { count: summaryStats.cancelledCount })}
                </Box>
              </MenuItem>
              <MenuItem value={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <RemoveShoppingCartIcon fontSize="small" color="warning" />
                  {t('restaurantOrdersReport.tabs.productCancellations', { count: summaryStats.productCancellationsCount })}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Desktop Tabs (md+) */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            borderBottom: 1, 
            borderColor: 'divider', 
            bgcolor: 'grey.50' 
          }}
        >
          <Tab
            icon={<TableRestaurantIcon />}
            iconPosition="start"
            label={t('restaurantOrdersReport.tabs.activeTables', { count: summaryStats.activeCount })}
          />
          <Tab
            icon={<CheckCircleIcon />}
            iconPosition="start"
            label={t('restaurantOrdersReport.tabs.completedOrders', { count: summaryStats.completedCount })}
          />
          <Tab
            icon={<CancelIcon />}
            iconPosition="start"
            label={t('restaurantOrdersReport.tabs.receiptCancellations', { count: summaryStats.cancelledCount })}
            sx={{ color: 'error.main' }}
          />
          <Tab
            icon={<RemoveShoppingCartIcon />}
            iconPosition="start"
            label={t('restaurantOrdersReport.tabs.productCancellations', { count: summaryStats.productCancellationsCount })}
            sx={{ color: 'warning.main' }}
          />
        </Tabs>

        {/* Active Sales Tab */}
        <TabPanel value={activeTab} index={0}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : filteredActiveSales.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
              <TableRestaurantIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t('restaurantOrdersReport.empty.activeTables')}</Typography>
            </Box>
          ) : (
            <>
              {/* 📱 Mobile Card View (xs, sm) */}
              <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, p: 2 }}>
                {paginatedActiveSales.map((sale) => {
                  const openedAtTurkeyTime = new Date(sale.OpenedAt).getTime() + (3 * 60 * 60 * 1000);
                  const duration = Math.round((Date.now() - openedAtTurkeyTime) / 60000);
                  const screen = restaurantScreens.find(s => s.id === sale.RestaurantScreenId);
                  
                  return (
                    <Grid item xs={6} key={sale.Id}>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: 'warning.main',
                          bgcolor: 'warning.50',
                          '&:hover': { 
                            boxShadow: 4,
                            borderColor: 'warning.dark'
                          }
                        }}
                      >
                        {/* Masa Başlığı */}
                        <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                          <TableRestaurantIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                          <Typography variant="subtitle2" fontWeight="bold" color="warning.dark">
                            {sale.TableName || t('restaurantOrdersReport.table.tableWithId', { id: sale.TableId })}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* Şube */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.branch')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">{screen?.name || '-'}</Typography>
                        </Box>

                        {/* Açılış */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.openedAt')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">{formatDate(sale.OpenedAt, true)}</Typography>
                        </Box>

                        {/* Süre */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.duration')}:</Typography>
                          <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                            label={formatDurationLabel(duration)}
                            size="small"
                            color={duration > 60 ? 'warning' : 'default'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>

                        {/* Ürün Sayısı */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.productCount')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">{sale.ItemCount || 0}</Typography>
                        </Box>

                        {/* Toplam */}
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.total')}:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {formatCurrency(sale.TotalAmount || 0)}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* İşlemler */}
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title={t('restaurantOrdersReport.tooltips.orderDetails')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewActiveDetails(sale)}
                              sx={{ bgcolor: 'primary.50' }}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('restaurantOrdersReport.tooltips.waiterActions')}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleViewWaiterActions(sale.WaiterActionsJson, `${t('restaurantOrdersReport.labels.table')}: ${sale.TableName || sale.TableId}`)}
                              sx={{ bgcolor: 'secondary.50' }}
                            >
                              <HistoryIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {/* 🖥️ Desktop Table View (md+) */}
              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t('restaurantOrdersReport.table.table')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t('restaurantOrdersReport.table.branch')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t('restaurantOrdersReport.table.openedAt')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t('restaurantOrdersReport.table.duration')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t('restaurantOrdersReport.table.productCount')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t('restaurantOrdersReport.table.subTotal')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>{t('restaurantOrdersReport.table.total')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }} align="center">{t('restaurantOrdersReport.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedActiveSales.map((sale) => {
                      // OpenedAt'e +3 saat ekle (UTC'den Türkiye saatine çevir)
                      const openedAtTurkeyTime = new Date(sale.OpenedAt).getTime() + (3 * 60 * 60 * 1000);
                      const duration = Math.round((Date.now() - openedAtTurkeyTime) / 60000);
                      const screen = restaurantScreens.find(s => s.id === sale.RestaurantScreenId);
                      return (
                        <TableRow key={sale.Id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <TableRestaurantIcon fontSize="small" color="warning" />
                              <Typography fontWeight="medium">{sale.TableName || t('restaurantOrdersReport.table.tableWithId', { id: sale.TableId })}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{screen?.name || '-'}</Typography>
                          </TableCell>
                          <TableCell>{formatDate(sale.OpenedAt, true)}</TableCell>
                          <TableCell>
                            <Chip
                              icon={<AccessTimeIcon />}
                              label={formatDurationLabel(duration)}
                              size="small"
                              color={duration > 60 ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{sale.ItemCount || 0}</TableCell>
                          <TableCell>{formatCurrency(sale.SubTotal || 0)}</TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" color="primary.main">
                              {formatCurrency(sale.TotalAmount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={0.5} justifyContent="center">
                              <Tooltip title={t('restaurantOrdersReport.tooltips.orderDetails')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewActiveDetails(sale)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('restaurantOrdersReport.tooltips.waiterActions')}>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleViewWaiterActions(sale.WaiterActionsJson, `${t('restaurantOrdersReport.labels.table')}: ${sale.TableName || sale.TableId}`)}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredActiveSales.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t('restaurantOrdersReport.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              />
              {/* ✅ Küçük ekranlar için boşluk */}
              <Box sx={{ height: 80 }} />
            </>
          )}
        </TabPanel>

        {/* Completed Sales Tab */}
        <TabPanel value={activeTab} index={1}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : filteredCompletedSales.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t('restaurantOrdersReport.empty.completedOrders')}</Typography>
            </Box>
          ) : (
            <>
              {/* 📱 Mobile Card View (xs, sm) */}
              <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, p: 2 }}>
                {paginatedCompletedSales.map((sale) => {
                  const paymentInfo = getLocalizedPaymentMethod(sale.PaymentMethod);
                  const screen = restaurantScreens.find(s => s.id === sale.RestaurantScreenId);
                  
                  return (
                    <Grid item xs={6} key={sale.Id}>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: 'success.main',
                          bgcolor: 'success.50',
                          '&:hover': { 
                            boxShadow: 4,
                            borderColor: 'success.dark'
                          }
                        }}
                      >
                        {/* Masa Başlığı */}
                        <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                          <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                            {sale.TableName}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* Şube */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.branch')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">{screen?.name || '-'}</Typography>
                        </Box>

                        {/* Kapanış */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.closedAt')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">{formatDate(sale.CompletedAt, true)}</Typography>
                        </Box>

                        {/* Süre */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.duration')}:</Typography>
                          <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                            label={formatDurationLabel(sale.DurationMinutes)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>

                        {/* Ödeme */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.payment')}:</Typography>
                          <Chip
                            label={paymentInfo.label}
                            size="small"
                            color={paymentInfo.color}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>

                        {/* Kapatan */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.closedBy')}:</Typography>
                          <Box display="flex" alignItems="center" gap={0.3}>
                            <PersonIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight="medium">
                              {getWaiterNameFromActions(sale.WaiterActionsJson) !== '-' 
                                ? getWaiterNameFromActions(sale.WaiterActionsJson) 
                                : (sale.CompletedByName || '-')}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Toplam */}
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.total')}:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {formatCurrency(sale.TotalAmount)}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* İşlemler */}
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title={t('restaurantOrdersReport.tooltips.orderDetails')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(sale)}
                              sx={{ bgcolor: 'primary.50' }}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('restaurantOrdersReport.tooltips.waiterActions')}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleViewWaiterActions(sale.WaiterActionsJson, `${t('restaurantOrdersReport.labels.table')}: ${sale.TableName}`, sale.CancellationReason)}
                              sx={{ bgcolor: 'secondary.50' }}
                            >
                              <HistoryIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {/* 🖥️ Desktop Table View (md+) */}
              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }}>{t('restaurantOrdersReport.table.table')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }}>{t('restaurantOrdersReport.table.branch')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }}>{t('restaurantOrdersReport.table.closedAt')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }}>{t('restaurantOrdersReport.table.duration')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }}>{t('restaurantOrdersReport.table.total')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }}>{t('restaurantOrdersReport.table.payment')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }}>{t('restaurantOrdersReport.table.closedBy')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'success.main', color: 'white' }} align="center">{t('restaurantOrdersReport.table.detail')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCompletedSales.map((sale) => {
                      const paymentInfo = getLocalizedPaymentMethod(sale.PaymentMethod);
                      const screen = restaurantScreens.find(s => s.id === sale.RestaurantScreenId);
                      return (
                        <TableRow key={sale.Id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CheckCircleIcon fontSize="small" color="success" />
                              {sale.TableName}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{screen?.name || '-'}</Typography>
                          </TableCell>
                          <TableCell>{formatDate(sale.CompletedAt, true)}</TableCell>
                          <TableCell>
                            <Chip
                              icon={<AccessTimeIcon />}
                              label={formatDurationLabel(sale.DurationMinutes)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" color="success.main">
                              {formatCurrency(sale.TotalAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={paymentInfo.label}
                              size="small"
                              color={paymentInfo.color}
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <PersonIcon fontSize="small" color="action" />
                              {getWaiterNameFromActions(sale.WaiterActionsJson) !== '-' 
                                ? getWaiterNameFromActions(sale.WaiterActionsJson) 
                                : (sale.CompletedByName || '-')}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={0.5} justifyContent="center">
                              <Tooltip title={t('restaurantOrdersReport.tooltips.orderDetails')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewDetails(sale)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('restaurantOrdersReport.tooltips.waiterActions')}>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleViewWaiterActions(sale.WaiterActionsJson, `${t('restaurantOrdersReport.labels.table')}: ${sale.TableName}`, sale.CancellationReason)}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredCompletedSales.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t('restaurantOrdersReport.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              />
              {/* ✅ Küçük ekranlar için boşluk */}
              <Box sx={{ height: 80 }} />
            </>
          )}
        </TabPanel>

        {/* ✅ YENİ: Cancelled Sales Tab (Adisyon İptalleri) */}
        <TabPanel value={activeTab} index={2}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : filteredCancelledSales.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
              <CancelIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t('restaurantOrdersReport.empty.cancelledReceipts')}</Typography>
            </Box>
          ) : (
            <>
              {/* 📱 Mobile Card View (xs, sm) */}
              <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, p: 2 }}>
                {paginatedCancelledSales.map((sale) => {
                  const screen = restaurantScreens.find(s => s.id === sale.RestaurantScreenId);
                  const correctDuration = Math.round((new Date(sale.CompletedAt).getTime() - (new Date(sale.OpenedAt).getTime() + (3 * 60 * 60 * 1000))) / 60000);
                  
                  return (
                    <Grid item xs={6} key={sale.Id}>
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: 'error.main',
                          bgcolor: 'error.50',
                          '&:hover': { 
                            boxShadow: 4,
                            borderColor: 'error.dark'
                          }
                        }}
                      >
                        {/* Masa Başlığı */}
                        <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                          <CancelIcon sx={{ fontSize: 20, color: 'error.main' }} />
                          <Typography variant="subtitle2" fontWeight="bold" color="error.dark">
                            {sale.TableName}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* Şube */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.branch')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">{screen?.name || '-'}</Typography>
                        </Box>

                        {/* İptal Tarihi */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.cancelledAt')}:</Typography>
                          <Typography variant="caption" fontWeight="medium">{formatDate(sale.CompletedAt)}</Typography>
                        </Box>

                        {/* Süre */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.duration')}:</Typography>
                          <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                            label={formatDurationLabel(correctDuration)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>

                        {/* Durum */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.status')}:</Typography>
                          <Chip
                            label={sale.PaymentMethod === 'UNPAID' ? t('restaurantOrdersReport.payment.unpaid') : (sale.PaymentMethod || t('restaurantOrdersReport.payment.cancelled'))}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>

                        {/* İptal Eden */}
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.cancelledBy')}:</Typography>
                          <Box display="flex" alignItems="center" gap={0.3}>
                            <PersonIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight="medium">
                              {getWaiterNameFromActions(sale.WaiterActionsJson) !== '-' 
                                ? getWaiterNameFromActions(sale.WaiterActionsJson) 
                                : (sale.CompletedByName || '-')}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Tutar */}
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.amount')}:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="error.main" sx={{ textDecoration: 'line-through' }}>
                            {formatCurrency(sale.TotalAmount)}
                          </Typography>
                        </Box>

                        <Divider sx={{ mb: 1 }} />

                        {/* İşlemler */}
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title={t('restaurantOrdersReport.tooltips.orderDetails')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(sale)}
                              sx={{ bgcolor: 'primary.50' }}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('restaurantOrdersReport.tooltips.waiterActions')}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleViewWaiterActions(sale.WaiterActionsJson, `${t('restaurantOrdersReport.labels.table')}: ${sale.TableName}`, sale.CancellationReason)}
                              sx={{ bgcolor: 'secondary.50' }}
                            >
                              <HistoryIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              {/* 🖥️ Desktop Table View (md+) */}
              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }}>{t('restaurantOrdersReport.table.table')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }}>{t('restaurantOrdersReport.table.branch')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }}>{t('restaurantOrdersReport.table.cancelledAt')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }}>{t('restaurantOrdersReport.table.duration')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }}>{t('restaurantOrdersReport.table.amount')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }}>{t('restaurantOrdersReport.table.status')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }}>{t('restaurantOrdersReport.table.cancelledBy')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'error.main', color: 'white' }} align="center">{t('restaurantOrdersReport.table.detail')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCancelledSales.map((sale) => {
                      const screen = restaurantScreens.find(s => s.id === sale.RestaurantScreenId);
                      // İptal edilen siparişler için süreyi manuel hesapla (UTC farkı düzeltmesi)
                      const correctDuration = Math.round((new Date(sale.CompletedAt).getTime() - (new Date(sale.OpenedAt).getTime() + (3 * 60 * 60 * 1000))) / 60000);
                      return (
                        <TableRow key={sale.Id} hover sx={{ bgcolor: 'error.50' }}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <CancelIcon fontSize="small" color="error" />
                              {sale.TableName}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{screen?.name || '-'}</Typography>
                          </TableCell>
                          <TableCell>{formatDate(sale.CompletedAt)}</TableCell>
                          <TableCell>
                            <Chip
                              icon={<AccessTimeIcon />}
                              label={formatDurationLabel(correctDuration)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" color="error.main" sx={{ textDecoration: 'line-through' }}>
                              {formatCurrency(sale.TotalAmount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sale.PaymentMethod === 'UNPAID' ? t('restaurantOrdersReport.payment.unpaid') : (sale.PaymentMethod || t('restaurantOrdersReport.payment.cancelled'))}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <PersonIcon fontSize="small" color="action" />
                              {getWaiterNameFromActions(sale.WaiterActionsJson) !== '-' 
                                ? getWaiterNameFromActions(sale.WaiterActionsJson) 
                                : (sale.CompletedByName || '-')}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box display="flex" gap={0.5} justifyContent="center">
                              <Tooltip title={t('restaurantOrdersReport.tooltips.orderDetails')}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewDetails(sale)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('restaurantOrdersReport.tooltips.waiterActions')}>
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleViewWaiterActions(sale.WaiterActionsJson, `${t('restaurantOrdersReport.labels.table')}: ${sale.TableName}`, sale.CancellationReason)}
                                >
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredCancelledSales.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={t('restaurantOrdersReport.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              />
              {/* ✅ Küçük ekranlar için boşluk */}
              <Box sx={{ height: 80 }} />
            </>
          )}
        </TabPanel>

        {/* ✅ YENİ: Ürün İptalleri Tab */}
        <TabPanel value={activeTab} index={3}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : productCancellations.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
              <RemoveShoppingCartIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t('restaurantOrdersReport.empty.cancelledProducts')}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('restaurantOrdersReport.empty.productCancellationsHint')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* 📱 Mobile Card View (xs, sm) */}
              <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, p: 2 }}>
                {paginatedProductCancellations.map((cancellation, index) => (
                  <Grid item xs={6} key={`${cancellation.saleId}-${index}`}>
                    <Paper 
                      elevation={2} 
                      sx={{ 
                        p: 1.5, 
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'warning.main',
                        bgcolor: 'warning.50',
                        '&:hover': { 
                          boxShadow: 4,
                          borderColor: 'warning.dark'
                        }
                      }}
                    >
                      {/* Ürün Başlığı */}
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        <RemoveShoppingCartIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                        <Typography variant="subtitle2" fontWeight="bold" color="warning.dark" noWrap>
                          {cancellation.productName}
                        </Typography>
                      </Box>

                      <Divider sx={{ mb: 1 }} />

                      {/* İşlem */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.operation')}:</Typography>
                        <Chip
                          label={cancellation.actionType === 'remove_item' ? t('restaurantOrdersReport.cancellations.removed') : t('restaurantOrdersReport.cancellations.reduced')}
                          size="small"
                          color={cancellation.actionType === 'remove_item' ? 'error' : 'warning'}
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>

                      {/* Adet */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.quantity')}:</Typography>
                        {cancellation.actionType === 'remove_item' ? (
                          <Chip
                            label={t('restaurantOrdersReport.quantityWithUnit', { count: cancellation.quantity })}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ) : (
                          <Box display="flex" alignItems="center" gap={0.3}>
                            <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                              {cancellation.oldQuantity}
                            </Typography>
                            <Typography variant="caption">→</Typography>
                            <Typography variant="caption" fontWeight="bold" color="warning.main">
                              {cancellation.newQuantity}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Garson */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.waiter')}:</Typography>
                        <Box display="flex" alignItems="center" gap={0.3}>
                          <PersonIcon sx={{ fontSize: 12, color: 'primary.main' }} />
                          <Typography variant="caption" fontWeight="medium">{cancellation.waiterName}</Typography>
                        </Box>
                      </Box>

                      {/* Masa */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.table')}:</Typography>
                        <Typography variant="caption" fontWeight="medium">{cancellation.tableName}</Typography>
                      </Box>

                      {/* Şube */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.branch')}:</Typography>
                        <Typography variant="caption" fontWeight="medium">{cancellation.screenName}</Typography>
                      </Box>

                      {/* İptal Tarihi */}
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.date')}:</Typography>
                        <Typography variant="caption" fontWeight="medium">
                          {formatDate(cancellation.timestamp)}
                        </Typography>
                      </Box>

                      {/* Satış No */}
                      <Box display="flex" justifyContent="space-between" mb={cancellation.reason ? 0.5 : 0}>
                        <Typography variant="caption" color="text.secondary">{t('restaurantOrdersReport.labels.saleNumber')}:</Typography>
                        <Typography variant="caption" fontWeight="medium" color="primary.main">
                          #{cancellation.saleNumber}
                        </Typography>
                      </Box>

                      {/* Silme Nedeni */}
                      {cancellation.reason && (
                        <Box sx={{ mt: 0.5, p: 0.5, bgcolor: 'error.50', borderRadius: 0.5, border: '1px solid', borderColor: 'error.200' }}>
                          <Typography variant="caption" color="error.main" fontWeight="bold">
                            {t('restaurantOrdersReport.labels.reason')}: {cancellation.reason}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* 🖥️ Desktop Table View (md+) */}
              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.productName')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }} align="center">{t('restaurantOrdersReport.table.quantity')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.operation')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.waiter')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.table')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.branch')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.reason')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.cancelledAt')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'warning.main', color: 'white' }}>{t('restaurantOrdersReport.table.saleNumber')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProductCancellations.map((cancellation, index) => (
                      <TableRow key={`${cancellation.saleId}-${index}`} hover sx={{ bgcolor: 'warning.50' }}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <RemoveShoppingCartIcon fontSize="small" color="warning" />
                            <Typography fontWeight="medium">{cancellation.productName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {cancellation.actionType === 'remove_item' ? (
                            <Chip
                              label={t('restaurantOrdersReport.quantityWithUnit', { count: cancellation.quantity })}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          ) : (
                            <Box display="flex" alignItems="center" gap={0.5} justifyContent="center">
                              <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                {cancellation.oldQuantity}
                              </Typography>
                              <Typography variant="body2">→</Typography>
                              <Typography variant="body2" fontWeight="bold" color="warning.main">
                                {cancellation.newQuantity}
                              </Typography>
                              <Chip
                                label={t('restaurantOrdersReport.reducedWithCount', { count: Math.abs(cancellation.change || 0) })}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ ml: 0.5 }}
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cancellation.actionType === 'remove_item' ? t('restaurantOrdersReport.cancellations.removed') : t('restaurantOrdersReport.cancellations.quantityReduced')}
                            size="small"
                            color={cancellation.actionType === 'remove_item' ? 'error' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PersonIcon fontSize="small" color="primary" />
                            <Typography variant="body2">{cancellation.waiterName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <TableRestaurantIcon fontSize="small" color="action" />
                            <Typography variant="body2">{cancellation.tableName}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{cancellation.screenName || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          {cancellation.reason ? (
                            <Typography variant="body2" color="error.main" fontWeight="medium">
                              {cancellation.reason}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(cancellation.timestamp)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{cancellation.saleNumber}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={productCancellations.length}
                page={productCancellationsPage}
                onPageChange={handleProductCancellationsPageChange}
                rowsPerPage={10}
                rowsPerPageOptions={[10]}
                labelRowsPerPage={t('restaurantOrdersReport.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              />
              {/* ✅ Küçük ekranlar için boşluk */}
              <Box sx={{ height: 80 }} />
            </>
          )}
        </TabPanel>
      </Paper>

      {/* Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptLongIcon color="primary" />
            <Typography variant="h6">Sipariş Detayı - {selectedSale?.SaleNumber}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSale && (
            <Grid container spacing={2}>
              {/* Genel Bilgiler */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Genel Bilgiler
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Masa:</Typography>
                      <Typography variant="body2" fontWeight="medium">{selectedSale.TableName}</Typography>
                    </Box>
                    {selectedSale.TableGroupName && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Masa Grubu:</Typography>
                        <Typography variant="body2">{selectedSale.TableGroupName}</Typography>
                      </Box>
                    )}
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.labels.openedAt')}:</Typography>
                      <Typography variant="body2">{formatDate(selectedSale.OpenedAt, true)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.labels.closedAt')}:</Typography>
                      <Typography variant="body2">{formatDate(selectedSale.CompletedAt, !isCancelledPayment(selectedSale.PaymentMethod))}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.labels.duration')}:</Typography>
                      <Typography variant="body2">
                        {formatDurationLabel(
                          isCancelledPayment(selectedSale.PaymentMethod)
                            ? Math.round((new Date(selectedSale.CompletedAt).getTime() - (new Date(selectedSale.OpenedAt).getTime() + (3 * 60 * 60 * 1000))) / 60000)
                            : selectedSale.DurationMinutes
                        )}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Kapatan Garson:</Typography>
                      <Typography variant="body2">
                        {getWaiterNameFromActions(selectedSale.WaiterActionsJson) !== '-' 
                          ? getWaiterNameFromActions(selectedSale.WaiterActionsJson) 
                          : (selectedSale.CompletedByName || '-')}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Ödeme Bilgileri */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ödeme Bilgileri
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Ara Toplam:</Typography>
                      <Typography variant="body2">{formatCurrency(selectedSale.SubTotal)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">İndirim:</Typography>
                      <Typography variant="body2" color="error.main">
                        -{formatCurrency(selectedSale.DiscountAmount)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">KDV:</Typography>
                      <Typography variant="body2">{formatCurrency(selectedSale.TaxAmount)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="body1" fontWeight="bold">Toplam:</Typography>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        {formatCurrency(selectedSale.TotalAmount)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ pt: 1 }}>
                      <Typography variant="body2" color="text.secondary">Ödeme Yöntemi:</Typography>
                      <Chip
                        label={getLocalizedPaymentMethod(selectedSale.PaymentMethod).label}
                        size="small"
                        color={getLocalizedPaymentMethod(selectedSale.PaymentMethod).color}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Ürünler */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Sipariş Ürünleri
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Ürün</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="center">Adet</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">Birim Fiyat</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('restaurantOrdersReport.table.total')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parseItemsJson(selectedSale.ItemsJson).map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.productName}</TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.totalPrice)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* Garson İşlemleri */}
              {selectedSale.WaiterActionsJson && parseWaiterActionsJson(selectedSale.WaiterActionsJson).length > 0 && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Garson İşlemleri
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Garson</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>İşlem</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>{t('restaurantOrdersReport.table.detail')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Tarih/Saat</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parseWaiterActionsJson(selectedSale.WaiterActionsJson).map((action, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <PersonIcon fontSize="small" color="primary" />
                                  {getWaiterName(action)}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={getActionTypeLabel(action.action || action.actionType)} 
                                  size="small" 
                                  color="info"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                {getActionDetails(action)}
                              </TableCell>
                              <TableCell>
                                {getActionTime(action) ? formatDate(getActionTime(action)!, true) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              )}

              {/* İptal Nedeni */}
              {isCancelledPayment(selectedSale.PaymentMethod) && selectedSale.CancellationReason && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50', borderColor: 'error.main' }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CancelIcon color="error" fontSize="small" />
                      <Typography variant="subtitle2" color="error.main" fontWeight="bold">
                        İptal Nedeni
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="error.main" fontWeight="medium">
                      {selectedSale.CancellationReason}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Notlar */}
              {selectedSale.Notes && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notlar
                    </Typography>
                    <Typography variant="body2">{selectedSale.Notes}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsModal}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Aktif Masa Detay Modalı */}
      <Dialog
        open={activeDetailsModalOpen}
        onClose={handleCloseActiveDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TableRestaurantIcon color="warning" />
            <Typography variant="h6">
              Masa Detayı - {selectedActiveSale?.TableName || `Masa ${selectedActiveSale?.TableId}`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedActiveSale && (
            <Grid container spacing={2}>
              {/* Masa Bilgileri */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Masa Bilgileri
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Masa:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedActiveSale.TableName || `Masa ${selectedActiveSale.TableId}`}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.labels.openedAt')}:</Typography>
                      <Typography variant="body2">{formatDate(selectedActiveSale.OpenedAt, true)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">{t('restaurantOrdersReport.labels.duration')}:</Typography>
                      <Typography variant="body2">
                        {formatDurationLabel(Math.round((Date.now() - (new Date(selectedActiveSale.OpenedAt).getTime() + (3 * 60 * 60 * 1000))) / 60000))}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Ürün Sayısı:</Typography>
                      <Typography variant="body2">{activeSaleItems.length || selectedActiveSale.ItemCount || 0}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary">Ara Toplam:</Typography>
                      <Typography variant="body2">{formatCurrency(selectedActiveSale.SubTotal || 0)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1" fontWeight="bold">Toplam:</Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary.main">
                        {formatCurrency(selectedActiveSale.TotalAmount || 0)}
                      </Typography>
                    </Box>
                    {selectedActiveSale.Notes && (
                      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">Not:</Typography>
                        <Typography variant="body2">{selectedActiveSale.Notes}</Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Açan Bilgisi */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Açan Bilgisi
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Açan:</Typography>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight="medium">
                          {selectedActiveSale.OpenedBy || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Sipariş Ürünleri */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Sipariş Ürünleri
                  </Typography>
                  {loadingActiveSaleItems ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={30} />
                    </Box>
                  ) : activeSaleItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" py={2}>
                      Henüz ürün eklenmemiş
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ürün</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="center">Adet</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">Birim Fiyat</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }} align="right">{t('restaurantOrdersReport.table.total')}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ekleyen</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {activeSaleItems.map((item, index) => (
                            <TableRow key={item.Id || index}>
                              <TableCell>{item.ProductName}</TableCell>
                              <TableCell align="center">{item.Quantity}</TableCell>
                              <TableCell align="right">{formatCurrency(item.UnitPrice)}</TableCell>
                              <TableCell align="right">{formatCurrency(item.TotalPrice || item.UnitPrice * item.Quantity)}</TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <PersonIcon fontSize="small" color="action" />
                                  <Typography variant="body2">{item.AddedBy || '-'}</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActiveDetailsModal}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Garson İşlemleri Modalı */}
      <Dialog
        open={waiterActionsModalOpen}
        onClose={handleCloseWaiterActionsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="secondary" />
            <Typography variant="h6">Garson İşlemleri - {selectedWaiterActionsTitle}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* İptal Nedeni */}
          {selectedWaiterActionsCancellationReason && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'error.50', borderColor: 'error.main' }}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <CancelIcon color="error" fontSize="small" />
                <Typography variant="subtitle2" color="error.main" fontWeight="bold">
                  İptal Nedeni
                </Typography>
              </Box>
              <Typography variant="body1" color="error.main" fontWeight="medium">
                {selectedWaiterActionsCancellationReason}
              </Typography>
            </Paper>
          )}
          {selectedWaiterActions.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={200}>
              <HistoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Garson işlemi kaydı bulunamadı
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white' }}>Garson</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white' }}>İşlem</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white' }}>{t('restaurantOrdersReport.table.detail')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white' }}>Silme Nedeni</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white' }}>Tarih/Saat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedWaiterActions.map((action, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PersonIcon fontSize="small" color="primary" />
                          <Typography fontWeight="medium">{getWaiterName(action)}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getActionTypeLabel(action.action || action.actionType)} 
                          size="small" 
                          color="info"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {getActionDetails(action)}
                      </TableCell>
                      <TableCell>
                        {(action.action || action.actionType) === 'remove_item' && action.details?.reason ? (
                          <Typography variant="body2" color="error.main" fontWeight="medium">
                            {action.details.reason}
                          </Typography>
                        ) : (action.action || action.actionType) === 'remove_item' ? (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {getActionTime(action) ? formatDate(getActionTime(action)!, true) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWaiterActionsModal}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ScrollableContent>
  );
};

export default RestaurantOrdersReport;

